// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import partition from 'lodash/partition';

import {reporter} from 'common/report/error-reporting';
import {PropertyDefinition} from 'common/util/definitions-types';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';
import {apiClient} from 'common/util/api-client';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {getLocation} from 'common/util/url';
import {PROPERTY_RESOURCE_TYPE} from 'apps/lexicon/src/models/property-definition';
import {FetchOptions} from 'common/util/shared-client';
import {isQueryApiForLexiconPropertiesEnabled} from 'common/report/util/feature-sampling';

import {appApiUrl, queryApiUrl} from '../utils';
import {purgeFetchCache} from '../utils/fetch-cache';
import {DataDefinitionsClient} from './data-definitions-client';
import {PropertyDefinitionWithEvents} from './data-definitions-types';

class PropertyDefinitionsClient extends DataDefinitionsClient {
  public arrayFormatBracket = true;

  buildEndpoint({projectId, workspaceId, enableQueryApi = false}) {
    return enableQueryApi
      ? queryApiUrl(`data-definitions/`)
      : `${super.buildEndpoint({projectId, workspaceId})}/properties`;
  }

  /**
   * If category is defined, event is not, and vice-versa.
   *
   * Category has two possible values:
   *   user: grab user properties
   *   event: grab event properties
   *   super: grab properties present on the majority of events
   */
  buildQueryParams(
    options: {
      id?: number;
      name?: string;
      category?: string;
      event?: string;
      includeCustom?: boolean;
      useQueryCache?: boolean;
      includeDensity?: boolean;
      dataGroupId?: string;
    } = {},
  ) {
    const {
      id,
      name,
      category,
      event,
      includeCustom = false,
      useQueryCache = null,
      includeDensity = null,
      dataGroupId = null,
    } = options;
    // "events" is a common mistake
    // the API is case sensitive, but our frontend javascript feeds in lowercase values
    const resourceType = category === `events` ? `Event` : capitalize(category) || undefined;
    const params = {
      id,
      name,
      resourceType,
      event,
      includeCustom,
      ...(dataGroupId ? {dataGroupId} : {}),
    };
    if (useQueryCache !== null) {
      params[`use_query_cache`] = useQueryCache;
    }
    if (includeDensity !== null) {
      params[`includeDensity`] = true;
    }
    return params;
  }

  buildRequestBody({propertyDefinition}) {
    return JSON.stringify(propertyDefinition);
  }

  processResponse(response, params) {
    let result = super.processResponse(response, params);
    if (response.error) {
      throw new Error(response.error);
    }
    // the API is case sensitive, but our frontend javascript expects lowercase values
    // TODO: fix the inconsistency with `events` and `event` on the param.category
    // use `user` for now
    const resourceType = params.category === `user` ? `user` : `event`;
    if (Array.isArray(result)) {
      result = result.map((res) => {
        return {
          ...res,
          label: res.displayName ?? ``,
          dataGroupId: params.dataGroupId,
        };
      });
      // GOOT for projects with bad user properties see: https://mixpanel.slack.com/archives/C07KZ4NBB/p1605105643087200
      const [valid, invalid] = partition(result, (i) => i.name);
      if (invalid.length > 0) {
        reporter.warn(`unexpected property-definitions for ${resourceType}`, invalid);
      }
      return [...valid.map((def) => ({...def, resourceType}))];
    }
    return {...result, resourceType: result.resourceType.toLowerCase()};
  }
}

//** Deprecated. Migrate to using various PropertyDefinitionsResource variants */
export const propertyDefinitionsClient = new PropertyDefinitionsClient();

/** Api endpoint that does not specify resource type or custom. Use this for updates; not good for cache management */
export function buildGenericPropertyDefinitionsEndpoint(enableQueryApi = false) {
  return enableQueryApi
    ? queryApiUrl(`data_definitions/properties`)
    : appApiUrl(`data-definitions/properties`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
}

/** Api endpoint that is more specific, can be used for cache management */
function buildEndpoint(
  resourceType: string,
  includeCustom: boolean,
  enableQueryApi = false,
  params?: Map<string, any>,
) {
  const newSearchParams = new URLSearchParams();
  if (params) {
    params.forEach((value, key) => {
      newSearchParams.set(key, value);
    });
  }
  newSearchParams.set(`resourceType`, resourceType);
  newSearchParams.set(`includeCustom`, includeCustom.toString());
  return enableQueryApi
    ? `${buildGenericPropertyDefinitionsEndpoint(enableQueryApi)}`
    : // This assumes that appApiUrl() will never include query string params
      `${buildGenericPropertyDefinitionsEndpoint()}?${newSearchParams.toString()}`;
}

async function fetchPropertyDefinitions(fromFetchCache: boolean, resourceType: string, includeCustom: boolean) {
  const fetchOptions: FetchOptions = {
    headers: {
      'Request-URL': getLocation().href,
    },
    method: `GET`,
    fromFetchCache,
  };

  const enableQueryApi = isQueryApiForLexiconPropertiesEnabled(null);

  if (enableQueryApi) {
    fetchOptions.headers[`query_origin`] = `property-definitions-client`;
  }

  const queryParamsForQueryApi: {[key: string]: any} = {
    resourceType,
    // the presence of the includeCustom flag is interpreted as true
    ...(includeCustom ? {includeCustom} : {}),
    [`project_id`]: getProjectId(),
    [`workspace_id`]: getWorkspaceId(),
  };

  const result = await apiClient.get<PropertyDefinition[]>(
    buildEndpoint(resourceType, includeCustom, enableQueryApi),
    enableQueryApi ? queryParamsForQueryApi : null,
    fetchOptions,
  );
  const propertyDefinitions: PropertyDefinition[] = result ?? [];
  return propertyDefinitions.reduce((acc, propertyDefinition) => {
    acc[propertyDefinition.name] = propertyDefinition;
    return acc;
  }, {});
}

export async function update(updatePayload: Partial<PropertyDefinition>): Promise<PropertyDefinition> {
  return apiClient.patch<PropertyDefinition>(buildGenericPropertyDefinitionsEndpoint(), {
    ...updatePayload,
  });
}

export async function bulkUpdate(propDefUpdates: Array<Partial<PropertyDefinition>>) {
  return apiClient.patch<PropertyDefinition[]>(buildGenericPropertyDefinitionsEndpoint(), {properties: propDefUpdates});
}

export async function getPropertyDefinitionDetails(params: Map<string, any>): Promise<PropertyDefinitionWithEvents> {
  return apiClient.get<PropertyDefinitionWithEvents>(buildEndpoint(PROPERTY_RESOURCE_TYPE.EVENT, true, false, params));
}

export const eventPropertyDefinitionsResourceClient: ApiResourceClient<PropertyDefinition> = {
  index() {
    return {
      cache: fetchPropertyDefinitions(true, PROPERTY_RESOURCE_TYPE.EVENT, true),
      network: fetchPropertyDefinitions(false, PROPERTY_RESOURCE_TYPE.EVENT, true),
    };
  },

  get() {
    throw new Error(`Getting a single event definition is not implemented`);
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: buildEndpoint(PROPERTY_RESOURCE_TYPE.EVENT, true)});
  },
};

// public getEventPropertiesDefinitions(): Promise<ApiResponse> {
//   return this.api.get(`/properties`, {resourceType: PROPERTY_RESOURCE_TYPE.EVENT, includeCustom: true}, false);
// }

// public getUserPropertiesDefinitions(): Promise<ApiResponse> {
//   return this.api.get(`/properties`, {resourceType: PROPERTY_RESOURCE_TYPE.USER, includeCustom: true}, false);
// }

// public getGroupPropertiesDefinitions(dataGroupID): Promise<ApiResponse> {
//   return this.api.get(
//     `/properties`,
//     {resourceType: PROPERTY_RESOURCE_TYPE.USER, includeCustom: true, dataGroupId: dataGroupID},
//     false,
//   );
// }
